import classNames from 'classnames'
import { format, isSameDay, isSameMonth } from 'date-fns'
import React from 'react'
import * as styles from '../../styles'
import { useCalendar } from '../../useCalendar'

type DayProps = Readonly<{
  /**
   * The date to render, as midnight in the user’s local time zone. Because it’s
   * in the local zone the behavior should be consistent across all time zones,
   * regardless of the zone’s offset from UTC.
   */
  date: Date
  screenreaderFormatter: Intl.DateTimeFormat
  visualFormatter: Intl.DateTimeFormat
}>

export const Day = React.forwardRef<HTMLTableCellElement, DayProps>(
  ({ date, screenreaderFormatter, visualFormatter }, ref) => {
    const { focusDate, selectedDate, isDateAllowed } = useCalendar()

    const focused = isSameDay(date, focusDate)
    const selected = selectedDate && isSameDay(date, selectedDate)
    const [allowed, strictAllowed] = isDateAllowed(date)
    const today = isSameDay(date, new Date())
    const differentMonth = !isSameMonth(date, focusDate)

    return (
      <td
        // External code MUST NOT use the `is--X` classes.
        className={classNames('lapidary-calendar__day', styles.selectors.date, {
          'is--allowed': allowed,
          'is--strict-allowed': strictAllowed,
          'is--focused': focused,
          'is--sameMonth': !differentMonth,
          'is--selected': selected,
          'is--today': today,
        })}
        data-date={format(date, 'yyyy-MM-dd')}
        ref={ref}
        role="gridcell"
        tabIndex={focused ? 0 : -1}
      >
        <span aria-hidden>{visualFormatter.format(date)}</span>
        <span className="sr-only">{screenreaderFormatter.format(date)}</span>
      </td>
    )
  }
)
Day.displayName = 'Lapidary: Day ← Month ← Calendar'
